import React, { useEffect, useState } from 'react';
import api from '../../utils/api'; // configured axios instance with auth
import AdminNav from '../../components/admin/AdminNav';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function PendingAdminApprovals() {
  const [pendingAdmins, setPendingAdmins] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierFor, setSelectedSupplierFor] = useState({}); // map userId -> supplierId
  const [loadingApprovals, setLoadingApprovals] = useState({}); // map userId -> bool

  useEffect(() => {
    const load = async () => {
      try {
        const [usersRes, suppliersRes] = await Promise.all([
          api.get('/api/users', { params: { role: 'pending' } }),
          api.get('/api/suppliers'), // needed to assign supplier-admins
        ]);

        // filter out any superadmin entries if accidentally included
        let pending = Array.isArray(usersRes.data)
          ? usersRes.data
          : Array.isArray(usersRes.data.users)
          ? usersRes.data.users
          : [];

        pending = pending.filter(
          (u) => (u.role || '').toLowerCase() !== 'superadmin'
        );

        setPendingAdmins(pending);
        setSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : []);
      } catch (err) {
        console.error('Fetch error:', err);
        toast.error('Failed to load pending supplier-admins or suppliers.');
      }
    };
    load();
  }, []);

  const notifyChange = async (userId, action) => {
    try {
      await api.post(`/api/users/${userId}/notify-admin-change`, {
        action: action === 'approve' ? 'promote' : 'revoke',
      });
      toast.info(
        action === 'approve'
          ? 'Notification email sent for approval.'
          : 'Notification email sent for rejection.'
      );
    } catch (err) {
      console.warn('Failed to send notification email:', err);
      toast.warning('Role changed but failed to send notification email.');
    }
  };

  const approveAdmin = async (id) => {
    const user = pendingAdmins.find((u) => u._id === id);
    const supplierId = selectedSupplierFor[id];
    if (!supplierId) {
      toast.error('Please select a supplier to associate with this supplier-admin.');
      return;
    }

    setLoadingApprovals((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await api.post(`/api/users/approve-supplier-admin/${id}`, {
        supplierId,
      });
      if (res.status === 200) {
        setPendingAdmins((prev) => prev.filter((u) => u._id !== id));
        toast.success(
          user
            ? `Supplier-admin request for ${user.username} approved and linked to supplier.`
            : 'Supplier-admin approved successfully.'
        );
        await notifyChange(id, 'approve');
      } else {
        toast.error('Failed to approve supplier-admin.');
      }
    } catch (err) {
      console.error('Error approving admin:', err);
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Failed to approve supplier-admin.');
      }
    } finally {
      setLoadingApprovals((prev) => ({ ...prev, [id]: false }));
    }
  };

  const rejectAdmin = async (id) => {
    const user = pendingAdmins.find((u) => u._id === id);
    setLoadingApprovals((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await api.put(`/api/users/${id}/reject`);
      if (res.status === 200) {
        setPendingAdmins((prev) => prev.filter((u) => u._id !== id));
        toast.success(
          user
            ? `Admin request for ${user.username} rejected successfully.`
            : 'Admin rejected successfully.'
        );
        await notifyChange(id, 'reject');
      } else {
        toast.error('Failed to reject admin.');
      }
    } catch (err) {
      console.error('Error rejecting admin:', err);
      toast.error('Failed to reject admin.');
    } finally {
      setLoadingApprovals((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-5xl mx-auto bg-white shadow p-6 rounded-md">
        <h2 className="text-xl font-bold mb-4">Pending Supplier-Admin Approvals</h2>

        {pendingAdmins.length === 0 && <p>No pending supplier-admins.</p>}

        {pendingAdmins.map((user) => (
          <div
            key={user._id}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border rounded mb-3 gap-4"
          >
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{user.username}</p>
              <p className="text-sm text-gray-600">{user.email}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1">Associate Supplier</label>
                <select
                  value={selectedSupplierFor[user._id] || ''}
                  onChange={(e) =>
                    setSelectedSupplierFor((prev) => ({
                      ...prev,
                      [user._id]: e.target.value,
                    }))
                  }
                  className="border px-2 py-1 rounded"
                >
                  <option value="">Select supplier...</option>
                  {suppliers.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => approveAdmin(user._id)}
                  disabled={loadingApprovals[user._id]}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {loadingApprovals[user._id] ? 'Approving...' : 'Approve'}
                </button>
                <button
                  onClick={() => rejectAdmin(user._id)}
                  disabled={loadingApprovals[user._id]}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {loadingApprovals[user._id] ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
    </div>
  );
}
